import { Label } from '@kmgestao/ui';

import type { FormField } from '../../schemas.js';

export interface TextareaFieldProps {
  field: FormField;
  value: string;
  onChange: (next: string) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
  rows?: number;
}

/**
 * Campo de texto multilinha. O número de linhas vem do schema
 * (`rows` custom no DSL) ou default 4. As classes do textarea usam
 * tokens do DS (consistência com os outros Inputs) — evitamos
 * depender de um `Textarea` próprio antes do DS ter um.
 *
 * Por que 4 linhas como default: o template canônico (migration
 * 0039) usa `rows: 3` e `rows: 4` em textareas de diagnóstico;
 * começar com 4 cobre a maioria. Builder (M1-01d-ii) permitirá
 * customizar por campo.
 */
export function TextareaField({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
  rows = 4,
}: TextareaFieldProps) {
  const inputId = `${idPrefix}-${field.id}`;
  const descId = field.description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <textarea
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={field.placeholder}
        rows={rows}
        maxLength={field.maxlength}
        disabled={disabled}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
        className={[
          'flex w-full rounded-md border bg-background px-3 py-2 text-base',
          'ring-offset-background placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          error ? 'border-destructive' : 'border-input',
        ].join(' ')}
      />
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
